import React, { useEffect, useMemo } from 'react';

import { spacing } from '@rbilabs/universal-components/themes';
import { isBefore, subMinutes } from 'date-fns';
import { useStoreContext } from 'features/store';
import {
  getCurrentDateInStoreTimeZone,
  getSelectedStoreCurrentDate,
  getStoreOperatingHoursForServiceMode,
  isStoreOpen,
} from 'features/store/utils';
import { FlatList } from 'react-native';

import { useCart } from 'hooks/use-cart';
import { getIsMobileWeb } from 'hooks/use-media-query';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { formatTime, t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useServiceModeContext } from 'state/service-mode';
import { isNative } from 'utils/environment';

import { PressableTime, TimeText } from './styled';

const OPTIONS_LENGTH = 5;
const INTERVAL_MINS = 15;
const DAY_PART_END_AHEAD_MINUTES = 10;

const PickupTimeSelector = ({ isDisabled = false }: { isDisabled?: boolean }) => {
  const { store } = useStoreContext();
  const { pickupDate, setPickupDate } = useOrderContext();
  const { serviceMode } = useServiceModeContext();
  const { cartEntries } = useCart();
  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);
  const isMobileWeb = getIsMobileWeb();

  // Only desktop any size should show the scroll indicator
  const showsHorizontalScrollIndicator = !isMobileWeb || !isNative;

  // always reset to now when you mount cart (only happens if you leave the whole flow)
  useEffect(() => {
    setPickupDate(null);
  }, []);

  const timeOptions = useMemo<Date[]>(() => {
    const ORDER_NOW = (null as unknown) as Date;

    if (!serviceMode) {
      return [ORDER_NOW];
    }

    const hours = getStoreOperatingHoursForServiceMode(store, serviceMode);

    if (!hours) {
      return [ORDER_NOW];
    }

    const storeCurrentDate = getCurrentDateInStoreTimeZone(store);
    const currentMinutes = storeCurrentDate.getMinutes();
    const mod5Remainder = currentMinutes % 5;
    const epochRoundedTo5 = new Date(storeCurrentDate);
    epochRoundedTo5.setMinutes(currentMinutes - mod5Remainder);
    epochRoundedTo5.setSeconds(0);
    epochRoundedTo5.setMilliseconds(0);

    const areItemsAvailableInDatePart = (date: any) => {
      return !cartEntriesActiveDayParts.some(cartEntryMenuItemDayPart => {
        const dayPartLastCall = subMinutes(
          new Date(cartEntryMenuItemDayPart.endTime),
          DAY_PART_END_AHEAD_MINUTES
        );
        return isBefore(dayPartLastCall, date);
      });
    };

    const filteredOptions = Array.from({ length: OPTIONS_LENGTH })
      .map((_x, idx) => {
        const d = new Date(epochRoundedTo5);
        d.setMinutes(epochRoundedTo5.getMinutes() + (idx + 1) * INTERVAL_MINS);
        return d;
      })
      .filter(
        date =>
          isStoreOpen({
            currentStoreDate: date,
            store,
            serviceModes: [serviceMode],
          }) && areItemsAvailableInDatePart(date)
      );

    return [ORDER_NOW].concat(filteredOptions);
  }, [cartEntriesActiveDayParts, serviceMode, store]);

  return (
    <FlatList
      style={{
        paddingVertical: spacing.$3,
      }}
      testID="pickup-time-selector"
      data={timeOptions}
      keyExtractor={(item: Date) => item?.getTime()?.toString() || '0'}
      renderItem={({ item: date }: { item: Date }) => {
        const isSelected =
          (date === null && pickupDate === null) || date?.getTime() === pickupDate?.getTime();
        return (
          <PressableTime
            onPress={() => {
              const currentTime = getSelectedStoreCurrentDate(store.timezone || undefined);
              const timedFireMinutes = date
                ? Math.round((date?.getTime() - currentTime.getTime()) / 1000 / 60)
                : 0;
              logEvent(CustomEventNames.SELECT_TIMED_FIRE_OPTION, EventTypes.Other, {
                'Timed Fire Minutes': timedFireMinutes,
              });
              setPickupDate(date);
            }}
            $isSelected={isSelected}
            disabled={isDisabled}
          >
            <TimeText $isSelected={isSelected}>
              {date === null ? t('now') : formatTime(date)}
            </TimeText>
          </PressableTime>
        );
      }}
      horizontal
      showsHorizontalScrollIndicator={showsHorizontalScrollIndicator}
    />
  );
};

export default PickupTimeSelector;
